/***** frontend/src/components/Header.js *****/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Table, Badge } from 'react-bootstrap';
import { FaDoorOpen, FaUserCircle, FaCoins } from 'react-icons/fa';
import { RiExchangeFundsFill } from 'react-icons/ri';
import { BsChatDots } from 'react-icons/bs';
import { GiTwoCoins } from 'react-icons/gi';
import logo  from '../assets/logo.png';
import api   from '../api';
import { useAuth } from '../contexts/AuthContext';
import CustomerServiceModal from './CustomerServiceModal';
import '../styles/headerBackground.css';
// 导入清晰版本的CSS
import '../styles/headerBackgroundClean.css';

export default function Header () {
  const { user, logout, socket, unreadCount, setUnreadCount } = useAuth();
  const navigate = useNavigate();

  // 本地维护实时积分和银币
  const [points, setPoints] = useState(user?.currentPoints ?? 0);
  const [silver, setSilver] = useState(user?.silverCoins ?? 0);

  /* ===== 本组件自己的局部状态 ===== */
  const [showChoice,   setShowChoice]   = useState(false);   // “转账 / 明细” 入口
  const [showTransfer, setShowTransfer] = useState(false);   // 转账弹窗
  const [showList,     setShowList]     = useState(false);   // 明细弹窗
  const [amount, setAmount] = useState('');
  const [target, setTarget] = useState('');
  const [pwd,    setPwd]    = useState('');
  const [showCustomerService, setShowCustomerService] = useState(false);

  /* ——— 明细分页 —— */
  const [logs,        setLogs]        = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const limit = 10;  // 背景图片状态
  const [backgroundImage, setBackgroundImage] = useState(null);
  // 固定使用清晰模式（无遮罩效果）
  const useCleanMode = true;

  // 同步初始值
  useEffect(() => { setPoints(user?.currentPoints ?? 0); }, [user?.currentPoints]);
  useEffect(() => { setSilver(user?.silverCoins ?? 0); }, [user?.silverCoins]);
  // 订阅 socket 实时更新
  useEffect(() => {
    if (!socket) return;
    const onPoints = ({ newPoints }) => setPoints(newPoints);
    const onSilver = ({ silverCoins }) => setSilver(silverCoins);
    socket.on('pointsUpdate', onPoints);
    socket.on('silverUpdate', onSilver);
    return () => {
      socket.off('pointsUpdate', onPoints);
      socket.off('silverUpdate', onSilver);
    };
  }, [socket]);

  // 新增：自动监听 socket 的 newMessage，未读消息推送提醒 + 系统级通知
  useEffect(() => {
    if (!socket || !user) return;
    // 只要有新消息且当前客服弹窗没打开，则增加未读数并系统通知
    const handler = (msg) => {
      if (msg.to === user._id && msg.from !== user._id) {
        setUnreadCount && setUnreadCount(prev => (typeof prev === 'number' ? prev + 1 : 1));
        // 系统级通知
        if (window.Notification && Notification.permission === 'granted') {
          new Notification('新消息提醒', {
            body: msg.content
              ? (msg.content.length > 40 ? msg.content.slice(0, 40) + '...' : msg.content)
              : '您有新的客服消息',
            icon: '/favicon.ico'
          });
        } else if (window.Notification && Notification.permission !== 'denied') {
          Notification.requestPermission();
        }
      }
    };
    socket.on('newMessage', handler);
    return () => socket.off('newMessage', handler);
  }, [socket, user, setUnreadCount]);

  // 打开客服弹窗时清空未读数
  useEffect(() => {
    if (showCustomerService && setUnreadCount) setUnreadCount(0);
  }, [showCustomerService, setUnreadCount]);
  // 加载背景图片 - 简化版本
  useEffect(() => {
    // 尝试常见的背景图片路径
    const checkImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => resolve(null);
        img.src = src;
      });
    };

    const loadBackgroundImage = async () => {
      const imagePaths = ['/bj.gif', '/bj.jpg', '/bj.png', '/background.gif', '/background.jpg'];
      for (const path of imagePaths) {
        const result = await checkImage(path);
        if (result) {
          setBackgroundImage(result);
          break;
        }
      }
    };
    
    loadBackgroundImage();
  }, []);

  /* =========================================================
   * 3) 获取转账明细
   * =======================================================*/
  const fetchLogs = async (p=1)=>{
    setListLoading(true);
    try{
      const r = await api.get(`/api/users/transfer-logs?page=${p}&limit=${limit}`);
      setLogs(r.data.logs||[]);
      setPage(r.data.currentPage||1);
      setTotalPages(r.data.totalPages||1);
    }catch(e){
      alert(e.response?.data?.message||'获取明细失败');
    }finally{ setListLoading(false); }
  };

  /* =========================================================
   * 4) 清空转账记录
   * =======================================================*/
  const handleClearLogs = async ()=>{
    if(!window.confirm('确定要清空全部记录？')) return;
    try{
      await api.delete('/api/users/transfer-logs/clear');
      setLogs([]); setPage(1); setTotalPages(1);
      alert('已清空转账记录');
    }catch(e){
      alert(e.response?.data?.message||'清空失败');
    }
  };

  /* =========================================================
   * 5) 执行转账
   * =======================================================*/
  const handleDoTransfer = async ()=>{
    const nAmt = Number(amount);
    if(!target.trim() || !nAmt || nAmt<=0 || !pwd) return alert('请填写完整且金额>0');
    try{
      await api.post('/api/users/transfer',{
        targetUsername: target.trim(),
        amount : nAmt,
        password: pwd
      });
      alert('转账成功');
      setShowTransfer(false);
      setAmount(''); setTarget(''); setPwd('');
    }catch(e){
      alert(e.response?.data?.message||'转账失败');
    }
  };  /* =========================================================
   * 6) 渲染
   * =======================================================*/  // 动态背景样式，支持多种图片格式包括GIF
  const generateBackgroundStyle = () => {
    if (useCleanMode) {
      // 清晰模式：只有微弱的遮罩保证文字可读性
      const gradients = [
        'linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.05) 50%, rgba(0, 0, 0, 0.1) 100%)'
      ];
      
      if (backgroundImage) {
        gradients.push(`url('${backgroundImage}')`);
      } else {
        const fallbackImages = [
          '/bj.gif', '/bj.jpg', '/bj.png',
          '/background.gif', '/background.jpg',
          '/assets/bj.gif', '/assets/bj.jpg'
        ];
        gradients.push(...fallbackImages.map(img => `url('${img}')`));
      }
      
      return gradients.join(', ');
    } else {
      // 科技模式：保留原有的科技效果
      const gradients = [
        'linear-gradient(135deg, rgba(0, 20, 40, 0.1) 0%, rgba(0, 10, 30, 0.08) 50%, rgba(10, 0, 40, 0.1) 100%)',
        'radial-gradient(ellipse at 20% 80%, rgba(0, 255, 255, 0.03) 0%, transparent 60%)',
        'radial-gradient(ellipse at 80% 20%, rgba(0, 150, 255, 0.02) 0%, transparent 60%)'
      ];

      if (backgroundImage) {
        gradients.push(`url('${backgroundImage}')`);
      } else {
        const fallbackImages = [
          '/bj.gif', '/bj.jpg', '/bj.png',
          '/background.gif', '/background.jpg',
          '/assets/bj.gif', '/assets/bj.jpg'
        ];
        gradients.push(...fallbackImages.map(img => `url('${img}')`));
      }

      return gradients.join(', ');
    }
  };  const dynamicHeaderStyle = {
    ...styles.header,
    backgroundImage: generateBackgroundStyle(),
    backgroundSize: backgroundImage 
      ? (useCleanMode ? 'cover, cover' : 'cover, 400px 400px, 500px 500px, cover')
      : (useCleanMode ? 'cover, cover, cover, cover, cover, cover, cover' : 'cover, 400px 400px, 500px 500px, cover, cover, cover, cover, cover, cover, cover'),
    backgroundPosition: backgroundImage
      ? (useCleanMode ? 'left center, left center' : 'left center, 0 0, 100% 100%, left center')
      : (useCleanMode ? 'left center, left center, left center, left center, left center, left center, left center' : 'left center, 0 0, 100% 100%, left center, left center, left center, left center, left center, left center, left center'),
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'scroll'
  };

  // 移动设备特殊优化：强制使用更严格的左对齐显示
  if (window.innerWidth <= 768) {
    if (backgroundImage) {
      dynamicHeaderStyle.backgroundSize = useCleanMode ? 'auto 100%, auto 100%' : 'auto 100%, 200px 200px, 250px 250px, auto 100%';
      dynamicHeaderStyle.backgroundPosition = 'left center, left center';
    }
  }
  
  if (window.innerWidth <= 480) {
    if (backgroundImage) {
      dynamicHeaderStyle.backgroundSize = useCleanMode ? 'auto 100%, auto 100%' : 'auto 100%, 150px 150px, 200px 200px, auto 100%';
      dynamicHeaderStyle.backgroundPosition = 'left center, left center';
    }
  }

  // 动态选择CSS类名
  const headerClassName = useCleanMode ? 'header-background-clean' : 'header-background';
  return(
    <header className={headerClassName} style={dynamicHeaderStyle}>      {/* —— 左侧 Logo —— */}
      <div style={styles.leftSection}>
        <img src={logo} alt="logo" className="header-logo" style={styles.logoStyle}/>
        <h1 className="header-title" style={styles.title}>THE&nbsp;ONE</h1>
      </div>

      {/* —— 右侧用户信息 —— */}
      <div style={styles.rightSection}>
        {user ? (
          <>
            <table style={styles.infoTable}>
              <tbody>
                <tr>
                  <td style={styles.iconCell}><FaUserCircle size={15}/></td>
                  <td style={styles.tableCellValue}>{user.username}</td>
                </tr>
                <tr>
                  <td style={styles.iconCell}><FaCoins size={15} color="#ffc107"/></td>
                  <td style={styles.pointsValue}>{points.toFixed(1)}</td>
                </tr>
                <tr>
                  <td style={styles.iconCell}><GiTwoCoins size={15} color="#ccc"/></td>
                  <td style={styles.tableCellValue}>{silver.toFixed(1)}</td>
                </tr>
              </tbody>
            </table>

            {/* 转账/明细入口 */}
            <RiExchangeFundsFill
              size={20}
              title="转账 / 明细"
              style={styles.transferIcon}
              onClick={()=> setShowChoice(true)}
            />

            {/* 客服入口 */}
            <div
              title="联系客服"
              style={styles.iconBtn}
              onClick={() => setShowCustomerService(true)}
            >
              <BsChatDots size={20} />
              {unreadCount > 0 && (
                <Badge bg="danger" pill style={styles.badgeStyle}>
                  {unreadCount}
                </Badge>
              )}
            </div>

            {/* 退出 */}
            <FaDoorOpen
              size={20}
              title="退出登录"
              style={styles.logoutIcon}
              onClick={()=>{ logout(); navigate('/login',{replace:true}); }}
            />
          </>
        ):<div style={styles.notLoggedIn}>未登录</div>}
      </div>

      {/* ───────── Modal 1：选择操作 ───────── */}
      <Modal show={showChoice} onHide={()=>setShowChoice(false)} centered dialogClassName="glass-modal">
        <Modal.Header closeButton><Modal.Title>请选择操作</Modal.Title></Modal.Header>
        <Modal.Body style={{display:'flex',gap:20,justifyContent:'center'}}>
          <Button variant="success" onClick={()=>{ setShowChoice(false); setShowTransfer(true); }}>转账</Button>
          <Button variant="info"    onClick={()=>{ setShowChoice(false); fetchLogs(1); setShowList(true); }}>查看明细</Button>
        </Modal.Body>
      </Modal>

      {/* ───────── Modal 2：转账 ───────── */}
      <Modal show={showTransfer} onHide={()=>setShowTransfer(false)} centered dialogClassName="glass-modal">
        <Modal.Header closeButton><Modal.Title>积分转账</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <label className="form-label">收款用户名</label>
            <input className="form-control" value={target} onChange={e=>setTarget(e.target.value)}/>
          </div>
          <div className="mb-2">
            <label className="form-label">金额</label>
            <input type="number" className="form-control" value={amount} onChange={e=>setAmount(e.target.value)}/>
          </div>
          <div className="mb-2">
            <label className="form-label">登录密码</label>
            <input type="password" className="form-control" value={pwd} onChange={e=>setPwd(e.target.value)}/>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowTransfer(false)}>取消</Button>
          <Button variant="primary"   onClick={handleDoTransfer}>确认</Button>
        </Modal.Footer>
      </Modal>

      {/* ───────── Modal 3：转账明细 ───────── */}
      <Modal show={showList} onHide={()=>setShowList(false)} size="lg" centered dialogClassName="glass-modal">
        <Modal.Header closeButton><Modal.Title>转账明细</Modal.Title></Modal.Header>
        <Modal.Body>
          {listLoading? '加载中…':(
            logs.length===0? <p>暂无记录</p >:(
              <>
                <Table bordered hover size="sm" variant="dark">
                  <thead><tr><th>时间</th><th>类型</th><th>对方</th><th>金额</th></tr></thead>
                  <tbody>
                    {logs.map(l=>{
                      const t    = new Date(l.createdAt).toLocaleString();
                      const type = l.operationType==='transfer_out'?'转出':'转入';
                      return(
                        <tr key={l._id}>
                          <td>{t}</td><td>{type}</td><td>{l.peer}</td><td>{l.points}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {/* 分页 + 清空 */}
                <div style={{display:'flex',justifyContent:'space-between',marginTop:8}}>
                  <Button size="sm" disabled={page<=1} onClick={()=>fetchLogs(page-1)}>上一页</Button>
                  <div>
                    <Button
                      variant="danger"
                      size="sm"
                      style={{marginRight:8}}
                      onClick={handleClearLogs}
                    >
                      清空全部
                    </Button>
                    <Button size="sm" disabled={page>=totalPages} onClick={()=>fetchLogs(page+1)}>下一页</Button>
                  </div>
                </div>
                <div style={{textAlign:'center',marginTop:4}}>第 {page}/{totalPages} 页</div>
              </>
            )
          )}
        </Modal.Body>
      </Modal>

      {/* ───────── Modal 4：联系客服 ───────── */}
      <CustomerServiceModal show={showCustomerService} onHide={()=>setShowCustomerService(false)} />
    </header>
  );
}

/* ========== 样式 ========== */
const styles = {  header: {
    position: 'fixed', 
    top: 0, // Header从页面顶部开始，广播条覆盖在其上方
    left: 0, 
    right: 0, 
    height: window.innerWidth <= 480 ? 60 : window.innerWidth <= 768 ? 66 : 72, 
    border: 'none', // 移除所有边框
    display: 'flex', 
    alignItems: 'center',
    justifyContent: 'space-between', 
    padding: window.innerWidth <= 480 ? '0 12px' : window.innerWidth <= 768 ? '0 16px' : '0 24px', 
    margin: 0, // 确保没有外边距
    zIndex: 9999, // 低于广播条的10001
    boxShadow: 'none', // 移除阴影避免视觉间隙
    backdropFilter: window.innerWidth <= 768 ? 'none' : 'blur(8px)',
    overflow: 'hidden'
  },leftSection: { display: 'flex', alignItems: 'center' },
  logoStyle: { 
    height: window.innerWidth <= 480 ? 36 : window.innerWidth <= 768 ? 40 : 48, 
    marginRight: window.innerWidth <= 480 ? 8 : 12, 
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  },  title: {
    margin: 0, 
    fontSize: window.innerWidth <= 480 ? '0.9rem' : window.innerWidth <= 768 ? '1rem' : '1.25rem', 
    fontFamily: 'Consolas,"Courier New",monospace',
    color: '#fff', // 改为纯白色
    letterSpacing: window.innerWidth <= 480 ? 0.5 : 1,
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textShadow: '3px 3px 6px rgba(0,0,0,0.9), 0 0 12px rgba(0,0,0,0.8)' // 增强阴影确保可读性
  },rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: window.innerWidth <= 480 ? 8 : window.innerWidth <= 768 ? 12 : 16
  },  infoTable: {
    borderCollapse: 'collapse', 
    color: '#fff', 
    fontFamily: 'Consolas,"Courier New",monospace',
    textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)', // 增强阴影
    marginRight: 4, 
    fontSize: window.innerWidth <= 480 ? 11 : window.innerWidth <= 768 ? 12 : 13, 
    minWidth: 0
  },
  iconCell: { 
    padding: window.innerWidth <= 480 ? '1px 2px' : '2px 4px', 
    textAlign: 'center',
    color: '#fff', // 确保图标是白色
    textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
  },
  tableCellValue: { 
    padding: window.innerWidth <= 480 ? '1px 2px' : '2px 4px', 
    color: '#fff', // 改为纯白色，更清晰
    fontWeight: 'bold', 
    fontSize: window.innerWidth <= 480 ? 11 : window.innerWidth <= 768 ? 12 : 13,
    textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)' // 增强文字阴影
  },
  pointsValue: { 
    padding: window.innerWidth <= 480 ? '1px 2px' : '2px 4px', 
    color: '#ffff00', // 改为亮黄色，更突出
    fontWeight: 'bold', 
    fontFamily: 'LESLIE,sans-serif', 
    fontSize: window.innerWidth <= 480 ? 11 : window.innerWidth <= 768 ? 12 : 13,
    textShadow: '2px 2px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7), 0 0 12px rgba(255,255,0,0.3)' // 黄色发光效果
  },
  notLoggedIn: {
    fontFamily: 'Consolas,"Courier New",monospace', color: '#888', fontSize: '0.95rem', padding: '6px 8px'
  },
  logoutIcon: {
    cursor: 'pointer', color: '#0ff', filter: 'drop-shadow(0 0 4px rgba(0,255,255,.8))'
  },
  transferIcon: {
    cursor: 'pointer', color: '#0ff',
    filter: 'drop-shadow(0 0 4px rgba(0,255,255,.8))'
  },  iconBtn: {
    position: 'relative',
    cursor: 'pointer',
    color: '#0ff',
    filter: 'drop-shadow(0 0 4px rgba(0,255,255,.8))',
    width: window.innerWidth <= 480 ? 24 : window.innerWidth <= 768 ? 26 : 28,
    height: window.innerWidth <= 480 ? 24 : window.innerWidth <= 768 ? 26 : 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badgeStyle: {
    position: 'absolute',
    top: 0,
    right: 0,
    fontSize: window.innerWidth <= 480 ? 8 : 9,
    minWidth: window.innerWidth <= 480 ? 12 : 14,
    height: window.innerWidth <= 480 ? 12 : 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  }
};